import {
	ButtonBase,
	Divider,
	Modal,
	Stack,
	Switch,
	Typography,
	useTheme,
} from "@mui/material";
import i18next from "i18next";
import { Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useWatch } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import {
	type CloseProps,
	FinancesModal,
} from "web_myplann/Finances/Components/FinancesModal";
import { FooterButton } from "web_myplann/Finances/Components/FooterButton";
import { useIncomeForm } from "web_myplann/Finances/Forms/useIncomeForm";
import { useSyncFinanceState } from "web_myplann/Finances/Hooks/useSyncFinanceState";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import { MyPlannCzechResourcesDictionary } from "web_myplann/Infrastructure/Translations/CzechResourcesDictionary";
import { MyPlannEnglishResourcesDictionary } from "web_myplann/Infrastructure/Translations/EnglishResourcesDictionaty";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { IncrementalCounter } from "web_myplann/Shared/Components/IncrementalCounter";
import { MyPlannInput } from "web_myplann/Shared/Components/MyPlannInput";
import { ShowMoreButton } from "web_myplann/Shared/Components/ShowMoreButton";
import { MyPlannLayout } from "web_myplann/Shared/Header/MyPlannLayout";
import NavigationHeader from "web_myplann/Shared/Header/NavigationHeader";

const PageResources = Resources.Finances;

export const IncomePage = () => {
	const { t } = useResource();
	const theme = useTheme();
	const navigate = useNavigate();
	const location = useLocation();

	const { isHydrated } = useSyncFinanceState();

	const {
		incomeFields,
		incomeAppend,
		incomeRemove,
		setValue,
		watch,
		onSubmit,
		control,
	} = useIncomeForm();

	const [expanded, setExpanded] = useState(false);
	const [open, setOpen] = useState(false);
	const [editingIndex, setEditingIndex] = useState<number | undefined>(
		undefined,
	);
	const [previousFieldsLength, setPreviousFieldsLength] = useState(
		incomeFields.length,
	);

	const currentLanguage = i18next.language;
	const dictionary =
		currentLanguage === "en"
			? MyPlannEnglishResourcesDictionary
			: MyPlannCzechResourcesDictionary;
	const categories = dictionary.Finances.Income.descriptionExact.categories;

	const totalValue = useWatch({
		control,
		name: "totalValue",
	});

	const exact = useWatch({
		control,
		name: "exact",
	});

	useEffect(() => {
		if (exact && incomeFields.length === 0 && previousFieldsLength > 0) {
			setValue("exact", false);
		}
		setPreviousFieldsLength(incomeFields.length);
	}, [incomeFields.length, exact, setValue, previousFieldsLength]);

	const handleClose = (data?: CloseProps) => {
		setOpen(false);

		if (data) {
			if (editingIndex !== undefined) {
				setValue(`income.${editingIndex}.name`, data.name);
				setValue(`income.${editingIndex}.amount`, data.amount);
			} else {
				incomeAppend({ id: null, ...data });
			}
		}

		setEditingIndex(undefined);
	};

	const handleEditIncome = (index: number) => {
		setEditingIndex(index);
		setOpen(true);
	};

	const handleContinue = async () => {
		await onSubmit();
		navigate(Routing.Finances.Expenses.path(), {
			state: {
				from: location.state?.from,
				wizardDepth: (location.state?.wizardDepth ?? 0) + 1,
			},
		});
	};

	if (!isHydrated) {
		return null;
	}

	return (
		<MyPlannLayout
			disableContentPadding
			topBar={
				<NavigationHeader
					title={t(Resources.Finances.title)}
					defaultRoute={Routing.Finances.Intro.path()}
				/>
			}
		>
			<Stack py={1.6} gap={0.6} px={1.6}>
				<Typography
					variant="deprecatedH3"
					color={theme.palette.deprecated.grey[900]}
				>
					{t(PageResources.Income.titleTentatively)}
				</Typography>
				<Typography
					variant="deprecatedBody1"
					color={theme.palette.deprecated.grey[500]}
				>
					{t(PageResources.Income.descriptionTentative)}
				</Typography>
				<IncrementalCounter
					defaultValue={totalValue ?? 0}
					setValue={(value) => {
						setValue("totalValue", value);
					}}
					disabled={exact}
				/>
			</Stack>

			<Stack px={1.6} py={1.6}>
				<Divider sx={{ borderColor: theme.palette.deprecated.grey[200] }} />
			</Stack>

			<Stack
				direction={"row"}
				alignItems={"center"}
				justifyContent={"space-between"}
				px={1.6}
			>
				<Typography
					variant="deprecatedH3"
					color={theme.palette.deprecated.grey[900]}
				>
					{t(PageResources.Income.titleExact)}
				</Typography>
				<Switch
					sx={{
						transform: "scale(0.75)",
					}}
					checked={exact}
					onChange={() => setValue("exact", !exact)}
				/>
			</Stack>

			{exact ? (
				<>
					<Stack gap={1.2} pt={incomeFields.length > 0 ? 1.6 : 0} px={1.6}>
						{incomeFields.map((field, index) => (
							<Stack
								key={field.id}
								onClick={() => handleEditIncome(index)}
								sx={{ cursor: "pointer" }}
							>
								<MyPlannInput
									label={watch(`income.${index}.name`)}
									value={Number(watch(`income.${index}.amount`))}
									slotProps={{
										input: {
											readOnly: true,
											endAdornment: (
												<Stack
													direction="row-reverse"
													alignItems="center"
													gap={1.2}
												>
													<Typography
														variant="deprecatedBody1"
														color={theme.palette.deprecated.grey[500]}
													>
														{t(Resources.Common.CurrencySign)}
													</Typography>
													<Trash2
														size={theme.iconSize.sm}
														color={theme.palette.deprecated.error.main}
														onClick={(e) => {
															e.stopPropagation();
															incomeRemove(index);
														}}
													/>
												</Stack>
											),
										},
									}}
								/>
							</Stack>
						))}
					</Stack>
					<Stack pt={1.2} px={1.6}>
						<ShowMoreButton
							onClick={() => setOpen(true)}
							label={t(PageResources.Income.addIncome)}
						/>
					</Stack>
				</>
			) : (
				<>
					<Stack px={1.6}>
						<Typography
							variant="deprecatedBody1"
							color={theme.palette.deprecated.grey[500]}
						>
							{t(PageResources.Income.descriptionExact.intro)}
						</Typography>
					</Stack>

					<Stack py={1.6} px={1.6} pb={6}>
						<Typography
							variant="deprecatedBodyStrong"
							color={theme.palette.deprecated.grey[900]}
						>
							{t(PageResources.Income.subtitleExact)}
						</Typography>
						<Stack gap={1.2}>
							{categories
								.slice(0, expanded ? categories.length : 1.5)
								.map((category) => (
									<Stack key={category.title} gap={0.4}>
										<Typography
											variant="deprecatedBody1"
											color={theme.palette.deprecated.grey[500]}
										>
											{category.title}
										</Typography>
										<Stack component="ul" gap={0.2} pl={2} m={0}>
											{category.items.map((item: string) => (
												<Typography
													key={item}
													component="li"
													variant="deprecatedBody1"
													color={theme.palette.deprecated.grey[500]}
												>
													{item}
												</Typography>
											))}
										</Stack>
									</Stack>
								))}
						</Stack>

						<Stack
							pb={1.2}
							pt={0.4}
							onClick={() => setExpanded((prev) => !prev)}
						>
							<ButtonBase
								sx={{
									alignSelf: "flex-start",
								}}
							>
								<Typography
									variant="deprecatedBody2"
									color={theme.palette.primary.main}
								>
									{expanded
										? t(PageResources.Income.readLess)
										: t(PageResources.Income.readMore)}
								</Typography>
							</ButtonBase>
						</Stack>
					</Stack>
				</>
			)}

			<FooterButton
				title={t(Resources.Finances.Income.continue)}
				disabled={totalValue === 0}
				onClick={handleContinue}
			/>

			<Modal
				open={open}
				onClose={() => {
					setOpen(false);
					setEditingIndex(undefined);
				}}
			>
				<Stack
					position="absolute"
					top="50%"
					left="50%"
					width="95%"
					sx={{ transform: "translate(-50%, -50%)" }}
				>
					<FinancesModal
						onClose={handleClose}
						initialValues={
							editingIndex !== undefined
								? {
										name: watch(`income.${editingIndex}.name`),
										amount: Number(watch(`income.${editingIndex}.amount`)),
									}
								: undefined
						}
					/>
				</Stack>
			</Modal>
		</MyPlannLayout>
	);
};
